import { Component } from '@theme/component';

/**
 * Live countdown for announcement-bar promotions.
 *
 * @extends {Component}
 */
class AnnouncementCountdown extends Component {
  /**
   * @type {ReturnType<typeof setInterval> | undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stop();
  }

  get startTimestamp() {
    return Number(this.dataset.countdownStart || 0) * 1000;
  }

  get endTimestamp() {
    return Number(this.dataset.countdownEnd || 0) * 1000;
  }

  get labels() {
    return {
      prefix: this.dataset.countdownPrefix || '',
      daySingular: this.dataset.countdownDaySingular || 'DAY',
      dayPlural: this.dataset.countdownDayPlural || 'DAYS',
      hourSingular: this.dataset.countdownHourSingular || 'HR',
      hourPlural: this.dataset.countdownHourPlural || 'HRS',
      minuteSingular: this.dataset.countdownMinuteSingular || 'MIN',
      minutePlural: this.dataset.countdownMinutePlural || 'MINS',
      secondSingular: this.dataset.countdownSecondSingular || 'SEC',
      secondPlural: this.dataset.countdownSecondPlural || 'SECS',
    };
  }

  start() {
    this.stop();
    this.update();
    this.#interval = window.setInterval(() => this.update(), 1000);
  }

  stop() {
    if (!this.#interval) return;

    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  update() {
    const endTimestamp = this.endTimestamp;
    if (!endTimestamp) {
      this.hidden = true;
      this.textContent = '';
      this.stop();
      return;
    }

    const now = Date.now();
    const startTimestamp = this.startTimestamp;

    if (startTimestamp && now < startTimestamp) {
      this.hidden = true;
      this.textContent = '';
      return;
    }

    const remainingMilliseconds = endTimestamp - now;
    if (remainingMilliseconds <= 0) {
      this.hidden = true;
      this.textContent = '';
      this.stop();
      return;
    }

    this.hidden = false;
    this.textContent = this.formatCountdown(remainingMilliseconds);
  }

  formatCountdown(remainingMilliseconds) {
    const totalSeconds = Math.max(0, Math.floor(remainingMilliseconds / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const labels = this.labels;

    return `${labels.prefix} ${days} ${this.pluralize(days, labels.daySingular, labels.dayPlural)} ${hours} ${this.pluralize(hours, labels.hourSingular, labels.hourPlural)} ${minutes} ${this.pluralize(minutes, labels.minuteSingular, labels.minutePlural)} ${seconds} ${this.pluralize(seconds, labels.secondSingular, labels.secondPlural)}`.trim();
  }

  pluralize(value, singular, plural) {
    return value === 1 ? singular : plural;
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
